import { api } from "@/lib/api";

export type QualityMediaKind = "movie" | "tv" | "music";

export interface Quality {
  slug: string;
  name: string;
  source: string;
  resolution: number | null;
  bitrate_kbps: number | null;
  lossless: boolean;
  /** Representative size for one unit at this quality (one movie /
   *  episode / album), in MB. Already scaled per media kind. */
  typical_size_mb: number;
}

export interface QualityProfileResponse {
  /** Row id — used to attach custom formats. */
  id: number;
  media_kind: QualityMediaKind;
  /** All available qualities for this media kind, ordered best → worst. */
  catalog: Quality[];
  /** Slugs the user has enabled. */
  allowed: string[];
  /** Cutoff slug — at/above this quality, stop seeking upgrades. */
  cutoff: string | null;
  upgrade_allowed: boolean;
}

export interface UpdateProfileRequest {
  allowed: string[];
  cutoff: string | null;
  upgrade_allowed: boolean;
}

export const qualityApi = {
  getProfile: (kind: QualityMediaKind) =>
    api<QualityProfileResponse>(`/api/v1/quality/profiles/${kind}`),
  putProfile: (kind: QualityMediaKind, body: UpdateProfileRequest) =>
    api<QualityProfileResponse>(`/api/v1/quality/profiles/${kind}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------
// Custom formats (issue #2)
// ---------------------------------------------------------------------

export interface CustomFormat {
  id: number;
  name: string;
  /** Regex source — validated on the server with the `regex` crate. */
  pattern: string;
  score: number;
  media_kind: QualityMediaKind;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomFormatRequest {
  name: string;
  pattern: string;
  score: number;
  media_kind: QualityMediaKind;
}

export interface UpdateCustomFormatRequest {
  name?: string;
  pattern?: string;
  score?: number;
  media_kind?: QualityMediaKind;
}

export interface ProfileFormatsResponse {
  profile_id: number;
  format_ids: number[];
}

export const customFormatsApi = {
  list: () => api<CustomFormat[]>(`/api/v1/custom-formats`),
  create: (body: CreateCustomFormatRequest) =>
    api<CustomFormat>(`/api/v1/custom-formats`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: UpdateCustomFormatRequest) =>
    api<CustomFormat>(`/api/v1/custom-formats/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/custom-formats/${id}`, { method: "DELETE" }),

  listForProfile: (profileId: number) =>
    api<ProfileFormatsResponse>(
      `/api/v1/quality-profiles/${profileId}/formats`,
    ),
  setForProfile: (profileId: number, formatIds: number[]) =>
    api<ProfileFormatsResponse>(
      `/api/v1/quality-profiles/${profileId}/formats`,
      {
        method: "PUT",
        body: JSON.stringify({ format_ids: formatIds }),
      },
    ),
};
